import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { Button, message, Select, Slider } from "antd";
import { useLoaderData, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { ActivityOutletContext, ActivityScheduleConfig } from "../../interface";
import { updateActivitySchedule } from "../../apiBridge";
import '../../assets/Schedule.css';
import {
    SCHEDULE_HOURS,
    applySchedulePlacements,
    clearScheduleItems,
    cloneScheduleItems,
    scheduleItemInClearScope,
    courtEventsForDate,
    emptyCourtEvents,
    getVisibleScheduleTemplates,
    minutesToScheduleTime,
    scheduleCourtId,
    scheduleDayEndMinutes,
    scheduleItemFromEvent,
    scheduleTemplatesForCategory,
    scheduleTimeToMinutes,
    updateScheduleItem,
    upsertCourtEvent,
    validateScheduleRoundOrder,
    type CourtEvents,
    type ScheduleBoardEvent,
    type ScheduleTemplate,
} from './scheduleUtils';
import ScheduleCourt, { type SchedulePreviewInfo } from './_scheduleCourt';
import ScheduleAutoAlignModal from './_scheduleAutoAlignModal';
import ScheduleEditEventModal from './_scheduleEditEventModal';
import { ScheduleTemplateDrawer, ScheduleTemplateSidebar } from './_scheduleTemplateList';

const useDeviceType = () => {
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkDevice = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkDevice();
        window.addEventListener('resize', checkDevice);
        return () => window.removeEventListener('resize', checkDevice);
    }, []);

    return isMobile;
};

const getCourtWidth = (courtCount: number): string => {
    const templateListWidth = 250;
    const timeColumnWidth = 60;
    const minCourtWidth = 200;
    const availableWidth = `calc(100% - ${templateListWidth + timeColumnWidth}px)`;
    return `max(${minCourtWidth}px, calc(${availableWidth} / ${courtCount}))`;
};

const Schedule: React.FC = () => {
    const [messageApi, contextHolder] = message.useMessage();
    const navigate = useNavigate();
    const isMobile = useDeviceType();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [hasAnyMatches, setHasAnyMatches] = useState(false);
    const [selectedCategoryHasMatches, setSelectedCategoryHasMatches] = useState(false);
    const [autoAlignModalVisible, setAutoAlignModalVisible] = useState(false);

    const data = useLoaderData() as { "schedules": ActivityScheduleConfig[] } | null
    const { activity, categories, reloadData, setHasUnsavedChanges } = useOutletContext() as ActivityOutletContext

    const [savingState, setSavingState] = useState<boolean>(false)
    const [templates, setTemplates] = useState<ScheduleTemplate[]>([]);
    const [showAddedTemplates, setShowAddedTemplates] = useState(false);
    const visibleTemplates = useMemo(
        () => getVisibleScheduleTemplates(templates, showAddedTemplates),
        [templates, showAddedTemplates]
    );
    const nowDate = useRef(activity.options.start_time[0].date)

    const [pixelsPerMinute, setPixelsPerMinute] = useState<number>(3);

    const [activePreviewCourtId, setActivePreviewCourtId] = useState<string | null>(null);
    const [previewInfo, setPreviewInfo] = useState<SchedulePreviewInfo | null>(null);

    const [validStartTime, setValidStartTime] = useState<number>(60);
    const [validEndTime, setValidEndTime] = useState<number | null>(null);

    const [searchParams] = useSearchParams();
    const [nowCategory, setNowCategory] = useState<number>(() => {
        const categoryFromUrl = Number(searchParams.get('category'));
        return Number.isInteger(categoryFromUrl) && categoryFromUrl > 0 ? categoryFromUrl : 0;
    });
    const [currentDrag, setCurrentDrag] = useState<{
        type: 'template' | 'event';
        data: ScheduleTemplate | ScheduleBoardEvent;
    } | null>(null);
    const [originScheduleItems, setOriginScheduleItems] = useState<ActivityScheduleConfig[]>(
        () => cloneScheduleItems(data?.schedules || [])
    )
    const initialScheduleItemsRef = useRef<ActivityScheduleConfig[]>(cloneScheduleItems(data?.schedules || []))
    const [events, setEvents] = useState<CourtEvents>({});

    const [editingEvent, setEditingEvent] = useState<ScheduleBoardEvent | null>(null);
    const [editModalVisible, setEditModalVisible] = useState(false);

    useEffect(() => {
        const nextItems = cloneScheduleItems(data?.schedules || []);
        setOriginScheduleItems(nextItems);
        initialScheduleItemsRef.current = cloneScheduleItems(nextItems);
        setHasUnsavedChanges?.(false);
    }, [data?.schedules, setHasUnsavedChanges]);

    useEffect(() => {
        setEvents(emptyCourtEvents(activity.max_court_count));
    }, [activity.max_court_count]);

    useEffect(() => {
        const hasChanges = JSON.stringify(originScheduleItems) !== JSON.stringify(initialScheduleItemsRef.current);
        setHasUnsavedChanges?.(hasChanges);
    }, [originScheduleItems, setHasUnsavedChanges]);

    const updateTemplateUsed = useCallback((id: number, used: boolean) => {
        setTemplates(prev =>
            prev.map(t => t.id === id ? { ...t, used } : t)
        );
    }, []);

    const handleResetMatch = (id: number) => {
        setEvents(prev => {
            const newEvents = { ...prev };
            Object.keys(newEvents).forEach(courtId => {
                newEvents[courtId] = newEvents[courtId].filter(e => e.id !== id);
            });
            return newEvents;
        });

        updateTemplateUsed(id, false);
        setOriginScheduleItems(prevOrigin => updateScheduleItem(prevOrigin, id, scheduleItemFromEvent({
            courtId: '', start: 0, end: 0,
        }, '')));
    };

    const handleResetSchedule = (currentDateOnly: boolean) => {
        const scope = {
            categoryId: nowCategory || undefined,
            date: currentDateOnly ? nowDate.current : undefined,
        };

        setEvents(prev => {
            const next = { ...prev };
            Object.keys(next).forEach(courtId => {
                next[courtId] = next[courtId].filter(event => {
                    const item = originScheduleItems[event.id];
                    return !item || !scheduleItemInClearScope(item, scope);
                });
            });
            return next;
        });

        setTemplates(prev =>
            prev.map(template => {
                const item = originScheduleItems[template.id];
                if (!item || !scheduleItemInClearScope(item, scope)) {
                    return template;
                }
                return { ...template, used: false };
            })
        );

        setOriginScheduleItems(prev => clearScheduleItems(prev, scope));
        messageApi.success(
            currentDateOnly
                ? 'Matches on this date have been reset'
                : 'All matches have been reset'
        );
    };

    const updateCategoryTemplate = useCallback((categoryId = 0) => {
        setTemplates(scheduleTemplatesForCategory(originScheduleItems, categoryId));
    }, [originScheduleItems])

    const updateEventDate = useCallback((dateStr: string) => {
        setEvents(courtEventsForDate(originScheduleItems, dateStr, activity.max_court_count));
    }, [activity.max_court_count, originScheduleItems])

    const handleTimeRangeChange = useCallback((type: 'start' | 'end', timeString: string | string[]) => {
        const timeStr = typeof timeString === 'string' ? timeString : (timeString.length > 0 ? timeString[0] : '');
        if (!timeStr) {
            if (type === 'end') {
                setValidEndTime(null);
            }
            return;
        }

        const minutes = scheduleTimeToMinutes(timeStr);
        if (type === 'start') {
            setValidStartTime(minutes);
        } else {
            setValidEndTime(minutes);
        }
    }, []);

    const updateTimeRange = useCallback((dateStr: string) => {
        const startTime = activity.options.start_time.find(item => item.date === dateStr)?.start_time;
        if (startTime) {
            handleTimeRangeChange('start', startTime);
        }
    }, [activity.options.start_time, handleTimeRangeChange]);

    useEffect(() => {
        updateCategoryTemplate(nowCategory)
        updateEventDate(nowDate.current)
        updateTimeRange(nowDate.current)

        setHasAnyMatches(originScheduleItems.length > 0);
        setSelectedCategoryHasMatches(
            originScheduleItems.some(item => (nowCategory === 0 || item.category_id === nowCategory))
        );
    }, [nowCategory, originScheduleItems, updateCategoryTemplate, updateEventDate, updateTimeRange])

    useEffect(() => {
        const categoryFromUrl = Number(searchParams.get('category'));
        if (Number.isInteger(categoryFromUrl) && categoryFromUrl > 0) {
            setNowCategory(categoryFromUrl);
        }
    }, [searchParams]);

    function changeCategory(categoryId: number) {
        setNowCategory(categoryId);
        const nextParams = new URLSearchParams(searchParams);
        if (categoryId > 0) {
            nextParams.set('category', String(categoryId));
        } else {
            nextParams.delete('category');
        }
        const query = nextParams.toString();
        window.history.replaceState({}, '', query ? `?${query}` : window.location.pathname);
    }

    function changeDate(dateStr: string) {
        nowDate.current = dateStr
        updateEventDate(dateStr);
        updateTimeRange(dateStr)
    }

    const getActualEndTime = (): number => scheduleDayEndMinutes(validEndTime);

    const saveChanges = async () => {
        setSavingState(true);

        const roundOrderError = validateScheduleRoundOrder(originScheduleItems);
        if (roundOrderError) {
            messageApi.error(roundOrderError);
            setSavingState(false);
            return;
        }

        const items = originScheduleItems.map(item => ({
            id: item.category_id,
            date: item.date,
            type: item.group_key,
            round: item.round,
            used: item.used,
            start_at: item.start_at,
            end_at: item.end_at,
            court: item.court,
        }));

        const res = await updateActivitySchedule(activity.id, items)
        if (res) {
            const responseData = await res.json()
            if (responseData) {
                if (responseData.error) {
                    messageApi.error(responseData.error);
                } else {
                    messageApi.success("save successfully")
                    reloadData('schedule');
                    setHasUnsavedChanges?.(false);
                    initialScheduleItemsRef.current = cloneScheduleItems(originScheduleItems);
                }
            }
        }

        setSavingState(false);
    }

    const applyBoardEvents = useCallback((placements: ScheduleBoardEvent[]) => {
        setEvents(prev => applySchedulePlacements(prev, [], placements, nowDate.current).events);
        setOriginScheduleItems(prev => applySchedulePlacements({}, prev, placements, nowDate.current).items);
        placements.forEach(placement => updateTemplateUsed(placement.id, true));
    }, [updateTemplateUsed]);

    const handleEventUpdate = useCallback((updatedEvent: ScheduleBoardEvent) => {
        setEvents(prev => upsertCourtEvent(prev, updatedEvent));
        setOriginScheduleItems(prevOrigin => updateScheduleItem(
            prevOrigin,
            updatedEvent.id,
            scheduleItemFromEvent(updatedEvent, nowDate.current),
        ));
        if (!updatedEvent.courtId) {
            updateTemplateUsed(updatedEvent.id, false);
        }
    }, [updateTemplateUsed]);

    const handleEventDrop = useCallback((newEvent: ScheduleBoardEvent) => {
        handleEventUpdate(newEvent);
        updateTemplateUsed(newEvent.id, true);
    }, [handleEventUpdate, updateTemplateUsed]);

    const openEditEvent = (event: ScheduleBoardEvent) => {
        setEditingEvent(event);
        setEditModalVisible(true);
        setDrawerVisible(false);
    };

    const handleTemplateSelect = (template: ScheduleTemplate) => {
        openEditEvent({
            id: template.id,
            title: template.title,
            courtId: scheduleCourtId(1),
            start: 0,
            end: 0,
            duration: template.duration,
        });
    };

    const handleEditConfirm = (event: ScheduleBoardEvent) => {
        handleEventUpdate(event);
        updateTemplateUsed(event.id, true);
        setEditModalVisible(false);
        setEditingEvent(null);
    };

    const templateListProps = {
        categories,
        nowCategory,
        onCategoryChange: changeCategory,
        templates,
        visibleTemplates,
        showAdded: showAddedTemplates,
        onShowAddedChange: setShowAddedTemplates,
        selectedCategoryHasMatches,
        onDragStart: (template: ScheduleTemplate) => setCurrentDrag({ type: 'template', data: template }),
        onReset: handleResetMatch,
        onResetSchedule: handleResetSchedule,
        currentDate: nowDate.current,
        onEdit: handleTemplateSelect,
    };

    return (
        <>
            {contextHolder}
            <DndProvider backend={HTML5Backend}>
                {!hasAnyMatches ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '8px',
                        margin: '20px'
                    }}>
                        <h2>No Schedule Blocks Available</h2>
                        <p>Finish Category Setup first so each round has parallel courts to schedule.</p>
                    </div>
                ) : (
                    <>
                        <div className="date-selector" style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ marginRight: '10px' }}>Date:</span>
                                    <Select style={{ width: '200px' }} defaultValue={nowDate.current} onChange={changeDate}>
                                        {activity.options.start_time?.map((start_time) => (
                                            <Select.Option key={start_time.date} value={start_time.date}>
                                                {start_time.date}
                                            </Select.Option>
                                        )) || [
                                                <Select.Option key="no-date" value="" disabled>
                                                    No Date Available
                                                </Select.Option>
                                            ]}
                                    </Select>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <Button onClick={() => saveChanges()} type="primary" loading={savingState}>Save Changes</Button>
                                    <Button type="link" onClick={() => setAutoAlignModalVisible(true)}>Quick Schedule</Button>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            if (nowCategory > 0) {
                                                navigate(`/activity/${activity.id}/bracket?category=${nowCategory}`);
                                                return;
                                            }
                                            navigate(`/activity/${activity.id}/bracket`);
                                        }}
                                    >
                                        View Bracket
                                    </Button>
                                    {isMobile && (
                                        <Button
                                            type="link"
                                            onClick={() => setDrawerVisible(true)}
                                        >
                                            Add Match
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '10px' }}>
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span style={{ marginRight: '10px' }}>Zoom: </span>
                                    <Slider
                                        min={1}
                                        max={4}
                                        step={0.5}
                                        defaultValue={pixelsPerMinute}
                                        style={{ width: '200px' }}
                                        onChange={(value) => setPixelsPerMinute(value)}
                                        marks={{ 1: '1x', 2: '2x', 3: '3x', 4: '4x' }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="schedule-container">
                            {!isMobile && <ScheduleTemplateSidebar {...templateListProps} />}

                            <div className="schedule">
                                <div
                                    className="time-column"
                                    style={{
                                        height: `${SCHEDULE_HOURS * 60 * pixelsPerMinute}px`
                                    }}
                                >
                                    {Array.from({ length: SCHEDULE_HOURS * 2 + 1 }).map((_, i) => (
                                        <div
                                            key={i}
                                            className="time-marker"
                                            style={{
                                                top: `${i * 30 * pixelsPerMinute}px`,
                                                fontWeight: i % 2 === 0 ? 'bold' : 'normal'
                                            }}
                                        >
                                            {minutesToScheduleTime(i * 30)}
                                        </div>
                                    ))}

                                    {Array.from({ length: SCHEDULE_HOURS + 1 }).map((_, i) => (
                                        <div
                                            key={`line-${i}`}
                                            className="hour-line"
                                            style={{
                                                top: `${i * 60 * pixelsPerMinute}px`
                                            }}
                                        />
                                    ))}
                                </div>
                                <div
                                    className="courts-container"
                                    style={{
                                        height: `${SCHEDULE_HOURS * 60 * pixelsPerMinute}px`,
                                        display: 'flex',
                                        gap: '16px',
                                        padding: '0',
                                        marginLeft: '0'
                                    }}
                                >
                                    {Array.from({ length: activity.max_court_count }, (_, i) => (
                                        <div
                                            key={scheduleCourtId(i + 1)}
                                            className="court-wrapper"
                                            style={{
                                                flex: `1 1 ${getCourtWidth(activity.max_court_count)}`,
                                                margin: '0'
                                            }}
                                        >
                                            <div className="court-content">
                                                <ScheduleCourt
                                                    courtId={scheduleCourtId(i + 1)}
                                                    title={`COURT ${i + 1}`}
                                                    events={events[scheduleCourtId(i + 1)] || []}
                                                    highlight={currentDrag?.data && 'courtId' in currentDrag.data
                                                        ? currentDrag.data.courtId === scheduleCourtId(i + 1)
                                                        : false}
                                                    onEventUpdate={handleEventUpdate}
                                                    onEventDrop={handleEventDrop}
                                                    pixelsPerMinute={pixelsPerMinute}
                                                    activePreviewCourtId={activePreviewCourtId}
                                                    setActivePreviewCourtId={setActivePreviewCourtId}
                                                    previewInfo={previewInfo}
                                                    setPreviewInfo={setPreviewInfo}
                                                    startTime={validStartTime}
                                                    endTime={getActualEndTime()}
                                                    onEventDoubleClick={openEditEvent}
                                                    maxCourtCount={activity.max_court_count}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {isMobile && (
                            <ScheduleTemplateDrawer
                                {...templateListProps}
                                visible={drawerVisible}
                                onClose={() => setDrawerVisible(false)}
                            />
                        )}

                        <ScheduleEditEventModal
                            visible={editModalVisible}
                            event={editingEvent}
                            maxCourtCount={activity.max_court_count}
                            events={events}
                            validStartTime={validStartTime}
                            validEndTime={getActualEndTime()}
                            onCancel={() => {
                                setEditModalVisible(false);
                                setEditingEvent(null);
                            }}
                            onConfirm={handleEditConfirm}
                        />

                        <ScheduleAutoAlignModal
                            visible={autoAlignModalVisible}
                            onClose={() => setAutoAlignModalVisible(false)}
                            onSuccess={() => setAutoAlignModalVisible(false)}
                            activity={activity}
                            categories={categories}
                            templates={templates}
                            originScheduleItems={originScheduleItems}
                            onPlaceEvents={applyBoardEvents}
                            endMinutes={getActualEndTime()}
                            currentDate={nowDate.current}
                            validStartTime={validStartTime}
                        />
                    </>
                )}
            </DndProvider>
        </>
    );
};

export default Schedule;
